import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import EditIcon from '@material-ui/icons/Edit'
import RoomIcon from '@material-ui/icons/Room'
import SearchIcon from '@material-ui/icons/Search'
import axios from 'axios'
import useAggregateVendors from './useAggregateVendors'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'

const LOCATION_TYPES = ['PIT', 'YARD', 'QUARRY', 'STOCKPILE_YARD', 'RETAIL_YARD', 'UNKNOWN']

const TYPE_COLORS = {
  PIT: '#8B4513',
  YARD: '#228B22',
  QUARRY: '#666666',
  STOCKPILE_YARD: '#B8860B',
  RETAIL_YARD: '#1565C0',
  UNKNOWN: '#1976d2'
}

function makeColorIcon (color) {
  return L.divIcon({
    className: '',
    html: `<div style="
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background-color: ${color};
      border: 2.5px solid #fff;
      box-shadow: 0 1px 4px rgba(0,0,0,0.45);
    "></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -12]
  })
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    height: 'calc(100vh - 116px)',
    overflow: 'hidden'
  },
  mapPane: {
    flex: '0 0 70%',
    position: 'relative'
  },
  sidebar: {
    flex: '0 0 30%',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden'
  },
  sidebarHeader: {
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  sidebarFilters: {
    padding: theme.spacing(1, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  sidebarList: {
    flex: 1,
    overflowY: 'auto'
  },
  locationItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    cursor: 'pointer'
  },
  typeDot: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: theme.spacing(0.75),
    flexShrink: 0,
    verticalAlign: 'middle'
  },
  typeLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.6)',
    zIndex: 1000
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center'
  },
  legend: {
    position: 'absolute',
    bottom: theme.spacing(3),
    left: theme.spacing(1),
    zIndex: 999,
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderRadius: 6,
    padding: theme.spacing(1, 1.5),
    boxShadow: '0 1px 6px rgba(0,0,0,0.2)',
    pointerEvents: 'none'
  },
  legendRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 2
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 6,
    flexShrink: 0
  }
}))

const EMPTY_FORM = {
  _id: null,
  vendorId: '',
  name: '',
  locationType: 'YARD',
  addressLine1: '',
  addressCity: '',
  addressState: 'AZ',
  addressZip: '',
  lat: '',
  lng: '',
  notes: ''
}

export default function PitsYardsTab () {
  const classes = useStyles()
  const { suppliers: aggregateVendors } = useAggregateVendors()

  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterVendor, setFilterVendor] = useState('')
  const [filterType, setFilterType] = useState('')

  // Create dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Delivery zone state
  const [deliveryZones, setDeliveryZones] = useState([])
  const [zonesLoading, setZonesLoading] = useState(false)
  const [zoneForm, setZoneForm] = useState({ zone: '', maxMilesOneWay: '', pricePerLoad: '', notes: '' })
  const [zoneSaving, setZoneSaving] = useState(false)
  const [zoneError, setZoneError] = useState('')

  // Leaflet map refs
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const markersRef = useRef([])

  const fetchLocations = useCallback(() => {
    setLoading(true)
    axios.get('/api/aggregate-dashboard/locations', { params: { limit: 500 } })
      .then((res) => setLocations(res.data))
      .catch((err) => console.error('[PitsYardsTab] fetch error:', err))
      .finally(() => setLoading(false))
  }, [])

  const fetchDeliveryZones = useCallback((locationId) => {
    if (!locationId) { setDeliveryZones([]); return }
    setZonesLoading(true)
    axios.get(`/api/aggregate-dashboard/locations/${locationId}/delivery-zones`)
      .then((res) => setDeliveryZones(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('[PitsYardsTab] zones fetch error:', err))
      .finally(() => setZonesLoading(false))
  }, [])

  useEffect(() => {
    fetchLocations()
  }, [fetchLocations])

  // Initialize Leaflet map
  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return

    mapRef.current = L.map(mapContainerRef.current).setView([33.45, -112.07], 10)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(mapRef.current)

    // Click-to-place handler
    mapRef.current.on('click', (e) => {
      setForm((f) => ({ ...f, lat: e.latlng.lat.toFixed(6), lng: e.latlng.lng.toFixed(6) }))
      setSaveError('')
      setDialogOpen(true)
    })

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [])

  // Update markers when locations or filters change
  useEffect(() => {
    if (!mapRef.current) return

    // Clear old markers
    markersRef.current.forEach((m) => m.remove())
    markersRef.current = []

    // Filter locations
    const filtered = locations.filter((loc) => {
      if (filterType && loc.locationType !== filterType) return false
      if (filterVendor) {
        const vendorName = (loc.vendorId && loc.vendorId.name) ? loc.vendorId.name : ''
        if (!vendorName.toLowerCase().includes(filterVendor.toLowerCase())) return false
      }
      return true
    })

    // Add markers for locations with coordinates
    filtered.forEach((loc) => {
      if (!loc.location || !loc.location.coordinates || loc.location.coordinates.length !== 2) return
      const [lng, lat] = loc.location.coordinates
      const color = TYPE_COLORS[loc.locationType] || TYPE_COLORS.UNKNOWN
      const vendorName = (loc.vendorId && loc.vendorId.name) ? loc.vendorId.name : ''

      const marker = L.marker([lat, lng], { icon: makeColorIcon(color) })
        .addTo(mapRef.current)
        .bindPopup(`
          <strong>${loc.name}</strong><br/>
          <span style="color:#666;font-size:0.85em">${loc.locationType}</span>
          ${vendorName ? `<br/><span style="font-size:0.85em">${vendorName}</span>` : ''}
          ${loc.notes ? `<br/><span style="font-size:0.8em;color:#555">${loc.notes}</span>` : ''}
        `)

      markersRef.current.push(marker)
    })
  }, [locations, filterVendor, filterType])

  const handleAddButton = () => {
    setForm(EMPTY_FORM)
    setSaveError('')
    setDeliveryZones([])
    setZoneForm({ zone: '', maxMilesOneWay: '', pricePerLoad: '', notes: '' })
    setZoneError('')
    setDialogOpen(true)
  }

  const handleEditLocation = (loc) => {
    const hasCoords = loc.location && loc.location.coordinates && loc.location.coordinates.length === 2
    setForm({
      _id: loc._id,
      vendorId: loc.vendorId?._id || loc.vendorId || '',
      name: loc.name || '',
      locationType: loc.locationType || 'UNKNOWN',
      addressLine1: loc.address?.line1 || '',
      addressCity: loc.address?.city || '',
      addressState: loc.address?.state || 'AZ',
      addressZip: loc.address?.postalCode || '',
      lat: hasCoords ? String(loc.location.coordinates[1]) : '',
      lng: hasCoords ? String(loc.location.coordinates[0]) : '',
      notes: loc.notes || ''
    })
    setSaveError('')
    fetchDeliveryZones(loc._id)
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    if (saving) return
    setDialogOpen(false)
    setForm(EMPTY_FORM)
    setSaveError('')
    setDeliveryZones([])
    setZoneForm({ zone: '', maxMilesOneWay: '', pricePerLoad: '', notes: '' })
    setZoneError('')
  }

  const handleFormChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }))
  }

  const handleSubmit = async () => {
    if (!form.name.trim()) { setSaveError('Name is required'); return }
    if (!form._id && !form.vendorId) { setSaveError('Vendor is required'); return }

    setSaving(true)
    setSaveError('')
    try {
      const payload = {
        name: form.name.trim(),
        locationType: form.locationType || 'UNKNOWN',
        notes: form.notes.trim() || undefined
      }
      if (!form._id) payload.vendorId = form.vendorId
      // Send address if any field is filled
      if (form.addressLine1 || form.addressCity || form.addressZip) {
        payload.address = {
          line1: form.addressLine1.trim(),
          city: form.addressCity.trim(),
          state: form.addressState.trim(),
          postalCode: form.addressZip.trim()
        }
      }
      // Send explicit coordinates if provided (takes priority over geocode)
      if (form.lat && form.lng) {
        payload.location = {
          type: 'Point',
          coordinates: [parseFloat(form.lng), parseFloat(form.lat)]
        }
      }
      if (form._id) {
        await axios.patch(`/api/aggregate-dashboard/locations/${form._id}`, payload)
      } else {
        await axios.post('/api/aggregate-dashboard/locations', payload)
      }
      setDialogOpen(false)
      setForm(EMPTY_FORM)
      fetchLocations()
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setSaveError(msg)
    } finally {
      setSaving(false)
    }
  }

  // ── Delivery zone handlers ──────────────────────────────────────────────────
  const handleAddZone = async () => {
    if (!form._id) { setZoneError('Save the location first'); return }
    if (!zoneForm.zone || !zoneForm.maxMilesOneWay || !zoneForm.pricePerLoad) {
      setZoneError('Zone #, max miles, and price per load are required')
      return
    }
    setZoneSaving(true)
    setZoneError('')
    try {
      await axios.put(`/api/aggregate-dashboard/locations/${form._id}/delivery-zones`, {
        vendorId: form.vendorId,
        zone: Number(zoneForm.zone),
        maxMilesOneWay: Number(zoneForm.maxMilesOneWay),
        pricePerLoad: Number(zoneForm.pricePerLoad),
        notes: zoneForm.notes
      })
      setZoneForm({ zone: '', maxMilesOneWay: '', pricePerLoad: '', notes: '' })
      fetchDeliveryZones(form._id)
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setZoneError(msg)
    } finally {
      setZoneSaving(false)
    }
  }

  const handleDeleteZone = async (zoneId) => {
    try {
      await axios.delete(`/api/aggregate-dashboard/locations/${form._id}/delivery-zones/${zoneId}`)
      fetchDeliveryZones(form._id)
    } catch (err) {
      console.error('[PitsYardsTab] delete zone error:', err)
    }
  }

  const [geocoding, setGeocoding] = useState(false)
  const [geocodingAll, setGeocodingAll] = useState(false)
  const [geocodeAllResult, setGeocodeAllResult] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null) // { _id, name, fromList: bool }

  const handleGeocode = async () => {
    if (!form._id) return
    setGeocoding(true)
    setSaveError('')
    try {
      const res = await axios.post(`/api/aggregate-dashboard/locations/${form._id}/geocode`)
      setForm((f) => ({ ...f, lat: String(res.data.lat), lng: String(res.data.lng) }))
      fetchLocations()
    } catch (err) {
      const msg = err.response?.data?.error || err.message
      setSaveError(msg)
    } finally {
      setGeocoding(false)
    }
  }

  const missingCoordsCount = locations.filter(
    (loc) => !loc.location || !loc.location.coordinates || loc.location.coordinates.length !== 2
  ).length

  const handleGeocodeAll = async () => {
    setGeocodingAll(true)
    setGeocodeAllResult(null)
    try {
      const res = await axios.post('/api/aggregate-dashboard/locations/geocode-all')
      setGeocodeAllResult(res.data)
      fetchLocations()
    } catch (err) {
      console.error('[PitsYardsTab] geocode-all error:', err)
      setGeocodeAllResult({ error: err.response?.data?.error || err.message })
    } finally {
      setGeocodingAll(false)
    }
  }

  const handleRequestDelete = (loc, fromList = false) => {
    setDeleteTarget({ _id: loc._id || form._id, name: loc.name || form.name, fromList })
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = async (suppress) => {
    if (!deleteTarget) return
    setSaving(true)
    try {
      await axios.delete(`/api/aggregate-dashboard/locations/${deleteTarget._id}`, {
        data: { suppress }
      })
      setDeleteConfirmOpen(false)
      setDeleteTarget(null)
      if (!deleteTarget.fromList) {
        setDialogOpen(false)
        setForm(EMPTY_FORM)
      }
      fetchLocations()
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleSidebarClick = (loc) => {
    if (!mapRef.current) return
    if (loc.location && loc.location.coordinates && loc.location.coordinates.length === 2) {
      const [lng, lat] = loc.location.coordinates
      mapRef.current.setView([lat, lng], 14)
      // Open the marker popup
      const marker = markersRef.current.find((m) => {
        const pos = m.getLatLng()
        return Math.abs(pos.lat - lat) < 0.0001 && Math.abs(pos.lng - lng) < 0.0001
      })
      if (marker) marker.openPopup()
    }
  }

  // Filtered list for sidebar
  const filteredLocations = locations.filter((loc) => {
    if (filterType && loc.locationType !== filterType) return false
    if (filterVendor) {
      const vendorName = (loc.vendorId && loc.vendorId.name) ? loc.vendorId.name : ''
      if (!vendorName.toLowerCase().includes(filterVendor.toLowerCase())) return false
    }
    return true
  })

  const legendTypes = LOCATION_TYPES.filter((t) => locations.some((l) => l.locationType === t))

  return (
    <div className={classes.root}>
      {/* Map pane */}
      <div className={classes.mapPane}>
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

        {loading && (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={36} />
          </div>
        )}

        {/* Legend */}
        {legendTypes.length > 0 && (
          <div className={classes.legend}>
            <Typography style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, color: '#555' }}>
              Type
            </Typography>
            {legendTypes.map((t) => (
              <div key={t} className={classes.legendRow}>
                <div className={classes.legendDot} style={{ backgroundColor: TYPE_COLORS[t] || TYPE_COLORS.UNKNOWN }} />
                <Typography style={{ fontSize: '0.7rem', color: '#333' }}>{t}</Typography>
              </div>
            ))}
            <Divider style={{ margin: '4px 0' }} />
            <Typography style={{ fontSize: '0.65rem', color: '#888' }}>
              Click map to add
            </Typography>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className={classes.sidebar}>
        <div className={classes.sidebarHeader}>
          <Typography variant="subtitle2">
            {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''}
          </Typography>
          <div style={{ display: 'flex', gap: 6 }}>
            {missingCoordsCount > 0 && (
              <Tooltip title={`Geocode ${missingCoordsCount} locations missing coordinates`}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={geocodingAll ? <CircularProgress size={14} /> : <RoomIcon style={{ fontSize: 16 }} />}
                  onClick={handleGeocodeAll}
                  disabled={geocodingAll}
                  style={{ textTransform: 'none', fontSize: '0.7rem', color: '#f57c00', borderColor: '#f57c00' }}
                >
                  {geocodingAll ? 'Geocoding...' : `${missingCoordsCount} missing`}
                </Button>
              </Tooltip>
            )}
            {geocodeAllResult && !geocodeAllResult.error && (
              <Typography variant="caption" style={{ alignSelf: 'center', color: '#2e7d32', fontSize: '0.7rem' }}>
                {geocodeAllResult.success}/{geocodeAllResult.total} geocoded
                {geocodeAllResult.googleUsed > 0 ? ` (${geocodeAllResult.googleUsed} via Google)` : ''}
              </Typography>
            )}
            <Tooltip title="Add Location">
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddButton}
              >
                Add
              </Button>
            </Tooltip>
          </div>
        </div>

        <div className={classes.sidebarFilters}>
          <TextField
            size="small"
            placeholder="Filter by vendor"
            value={filterVendor}
            onChange={(e) => setFilterVendor(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 16, color: '#aaa' }} />
                </InputAdornment>
              )
            }}
            variant="outlined"
          />
          <TextField
            select
            size="small"
            label="Type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            variant="outlined"
          >
            <MenuItem value="">All types</MenuItem>
            {LOCATION_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
        </div>

        <div className={classes.sidebarList}>
          {loading ? (
            <Box display="flex" justifyContent="center" pt={4}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredLocations.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body2" color="textSecondary">
                No locations found.
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Click on the map or use "Add" to create one.
              </Typography>
            </div>
          ) : (
            <List disablePadding>
              {filteredLocations.map((loc) => {
                const vendorName = loc.vendorId && loc.vendorId.name ? loc.vendorId.name : ''
                const color = TYPE_COLORS[loc.locationType] || TYPE_COLORS.UNKNOWN
                const hasCoords = loc.location && loc.location.coordinates && loc.location.coordinates.length === 2
                return (
                  <ListItem
                    key={loc._id}
                    className={classes.locationItem}
                    dense
                    button
                    onClick={() => handleSidebarClick(loc)}
                  >
                    <ListItemText
                      primary={
                        <span>
                          <span
                            className={classes.typeDot}
                            style={{ backgroundColor: color }}
                          />
                          {loc.name}
                        </span>
                      }
                      secondary={
                        <span>
                          <span className={classes.typeLabel}>{loc.locationType}</span>
                          {vendorName && <span style={{ marginLeft: 6, fontSize: '0.75rem' }}>{vendorName}</span>}
                          {!hasCoords && (
                            <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#f57c00' }}>
                              (no coordinates)
                            </span>
                          )}
                        </span>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={() => handleEditLocation(loc)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton edge="end" size="small" onClick={() => handleRequestDelete(loc, true)}>
                        <CloseIcon fontSize="small" style={{ color: '#c62828' }} />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                )
              })}
            </List>
          )}
        </div>
      </div>

      {/* Create Location Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{form._id ? 'Edit Location' : 'Add Location'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 4 }}>
            <TextField
              select
              label="Vendor *"
              value={form.vendorId}
              onChange={handleFormChange('vendorId')}
              variant="outlined"
              size="small"
              fullWidth
            >
              {aggregateVendors.length === 0 && (
                <MenuItem value="" disabled>No aggregate vendors configured</MenuItem>
              )}
              {aggregateVendors.map((v) => (
                <MenuItem key={v._id} value={v._id}>{v.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Name *"
              value={form.name}
              onChange={handleFormChange('name')}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="e.g. Vulcan Materials - Peoria Pit"
            />

            <TextField
              select
              label="Location Type"
              value={form.locationType}
              onChange={handleFormChange('locationType')}
              variant="outlined"
              size="small"
              fullWidth
            >
              {LOCATION_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>

            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
              Address (auto-geocodes if no lat/lng provided)
            </Typography>
            <TextField
              label="Street Address"
              value={form.addressLine1}
              onChange={handleFormChange('addressLine1')}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="e.g. 16620 W Yuma Rd"
            />
            <Box display="flex" style={{ gap: 8 }}>
              <TextField
                label="City"
                value={form.addressCity}
                onChange={handleFormChange('addressCity')}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Goodyear"
              />
              <TextField
                label="State"
                value={form.addressState}
                onChange={handleFormChange('addressState')}
                variant="outlined"
                size="small"
                style={{ width: 80, flexShrink: 0 }}
                placeholder="AZ"
              />
              <TextField
                label="ZIP"
                value={form.addressZip}
                onChange={handleFormChange('addressZip')}
                variant="outlined"
                size="small"
                style={{ width: 100, flexShrink: 0 }}
                placeholder="85338"
              />
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginTop: 4 }}>
              <Typography variant="caption" color="textSecondary">
                Coordinates {form.lat && form.lng ? '' : '(none)'}
              </Typography>
              {form._id && form.addressLine1 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleGeocode}
                  disabled={geocoding}
                  style={{ textTransform: 'none', fontSize: '0.7rem', padding: '2px 10px' }}
                >
                  {geocoding ? <CircularProgress size={14} /> : 'Geocode Address'}
                </Button>
              )}
            </Box>
            <Box display="flex" style={{ gap: 8 }}>
              <TextField
                label="Latitude"
                value={form.lat}
                onChange={handleFormChange('lat')}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="33.4500"
                type="number"
              />
              <TextField
                label="Longitude"
                value={form.lng}
                onChange={handleFormChange('lng')}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="-112.0700"
                type="number"
              />
            </Box>

            <TextField
              label="Notes"
              value={form.notes}
              onChange={handleFormChange('notes')}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={2}
            />

            {/* Delivery Zones — only when editing */}
            {form._id && (
              <>
                <Divider style={{ marginTop: 12 }} />
                <Typography variant="subtitle2" style={{ marginTop: 12, marginBottom: 4 }}>
                  Delivery Zones
                </Typography>

                {zonesLoading ? (
                  <Box display="flex" justifyContent="center" py={1}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <>
                    {deliveryZones.length > 0 && (
                      <Box style={{ border: '1px solid #e0e0e0', borderRadius: 4, marginBottom: 8 }}>
                        <Box display="flex" style={{ padding: '6px 10px', backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
                          <Typography variant="caption" style={{ flex: '0 0 50px', fontWeight: 700 }}>Zone</Typography>
                          <Typography variant="caption" style={{ flex: '0 0 80px', fontWeight: 700 }}>Max Miles</Typography>
                          <Typography variant="caption" style={{ flex: '0 0 90px', fontWeight: 700 }}>$/Load</Typography>
                          <Typography variant="caption" style={{ flex: 1, fontWeight: 700 }}>Notes</Typography>
                          <Typography variant="caption" style={{ flex: '0 0 36px' }} />
                        </Box>
                        {deliveryZones.map((z) => (
                          <Box key={z._id} display="flex" alignItems="center" style={{ padding: '4px 10px', borderBottom: '1px solid #f0f0f0' }}>
                            <Typography variant="body2" style={{ flex: '0 0 50px', fontSize: '0.8rem' }}>{z.zone}</Typography>
                            <Typography variant="body2" style={{ flex: '0 0 80px', fontSize: '0.8rem' }}>{z.maxMilesOneWay} mi</Typography>
                            <Typography variant="body2" style={{ flex: '0 0 90px', fontSize: '0.8rem' }}>${z.pricePerLoad}</Typography>
                            <Typography variant="body2" style={{ flex: 1, fontSize: '0.75rem', color: '#666' }}>{z.notes || ''}</Typography>
                            <IconButton size="small" onClick={() => handleDeleteZone(z._id)} style={{ flex: '0 0 36px' }}>
                              <CloseIcon style={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Box display="flex" alignItems="flex-end" style={{ gap: 6 }}>
                      <TextField
                        label="Zone #"
                        value={zoneForm.zone}
                        onChange={(e) => setZoneForm((f) => ({ ...f, zone: e.target.value }))}
                        variant="outlined"
                        size="small"
                        type="number"
                        style={{ width: 70 }}
                        inputProps={{ min: 1 }}
                      />
                      <TextField
                        label="Max Miles"
                        value={zoneForm.maxMilesOneWay}
                        onChange={(e) => setZoneForm((f) => ({ ...f, maxMilesOneWay: e.target.value }))}
                        variant="outlined"
                        size="small"
                        type="number"
                        style={{ width: 90 }}
                        inputProps={{ min: 0, step: 1 }}
                      />
                      <TextField
                        label="$/Load"
                        value={zoneForm.pricePerLoad}
                        onChange={(e) => setZoneForm((f) => ({ ...f, pricePerLoad: e.target.value }))}
                        variant="outlined"
                        size="small"
                        type="number"
                        style={{ width: 90 }}
                        inputProps={{ min: 0, step: 5 }}
                      />
                      <TextField
                        label="Notes"
                        value={zoneForm.notes}
                        onChange={(e) => setZoneForm((f) => ({ ...f, notes: e.target.value }))}
                        variant="outlined"
                        size="small"
                        style={{ flex: 1 }}
                        placeholder="optional"
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={handleAddZone}
                        disabled={zoneSaving}
                        style={{ minWidth: 60, height: 40 }}
                      >
                        {zoneSaving ? <CircularProgress size={16} /> : 'Add'}
                      </Button>
                    </Box>
                    {zoneError && (
                      <Typography variant="caption" style={{ color: '#c62828', marginTop: 4 }}>
                        {zoneError}
                      </Typography>
                    )}
                  </>
                )}
              </>
            )}

            {saveError && (
              <Typography variant="caption" style={{ color: '#c62828' }}>
                {saveError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-between' }}>
          <div>
            {form._id && (
              <Button
                onClick={() => handleRequestDelete(form)}
                disabled={saving}
                style={{ color: '#c62828' }}
              >
                Delete
              </Button>
            )}
          </div>
          <div>
            <Button onClick={handleDialogClose} disabled={saving}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={saving}
            >
              {saving ? <CircularProgress size={18} /> : 'Save'}
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setDeleteTarget(null) }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Location</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Remove <strong>{deleteTarget?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Do you want us to <strong>never sync this location again</strong>? If so, it will be permanently
            hidden and won't come back on future syncs.
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            If you just want to remove it for now, it may reappear if the address is found on the next sync.
          </Typography>
        </DialogContent>
        <DialogActions style={{ flexDirection: 'column', alignItems: 'stretch', padding: '8px 24px 16px', gap: 8 }}>
          <Button
            variant="contained"
            onClick={() => handleConfirmDelete(true)}
            disabled={saving}
            style={{ backgroundColor: '#c62828', color: '#fff', textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Never Sync Again'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleConfirmDelete(false)}
            disabled={saving}
            style={{ textTransform: 'none' }}
          >
            Remove for Now
          </Button>
          <Button
            onClick={() => { setDeleteConfirmOpen(false); setDeleteTarget(null) }}
            disabled={saving}
            style={{ textTransform: 'none', color: '#666' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
